import * as d3 from 'd3';
import { BarChartConfig } from '../data/BarChartConfig';
import { ChartDataType } from '../data/ChartDataType';
import { LineChartConfig } from '../data/LineChartConfig';
import { ChartData } from '../data/ChartData';
import { RendererConfig } from './RendererConfig';
import { ChartRendererContext } from './ChartRendererContext';

export class BandAxisRenderer {
  static MAX_X_LABEL_LENGTH = 70;

  static MAX_Y_LABEL_LENGTH = 100;

  constructor(
    private gElement: d3.Selection<SVGGElement, unknown, null, undefined>,
    private context: ChartRendererContext,
    private config: LineChartConfig | BarChartConfig,
    private centerLabels: boolean
  ) {}

  public renderX(width: number, height: number, limit?: number): { scale: d3.ScaleBand<string> } {
    const xAxisHeight: number = this.calcXHeight();

    const x = d3.scaleBand().range([0, width]).paddingInner(0);
    const primaryData = this.config.getDataArray(ChartDataType.primary);

    x.domain(primaryData!.getData(limit).map((d: ChartData) => d.label!));

    if (this.config.xAxisConfig.hide) {
      // return scale but do not render it
      return { scale: x };
    }

    const xAxis = d3.axisBottom(x).tickFormat(value => this.context.attributeFormatter(value) ?? '');

    const gContainer = this.gElement.append('g').attr('class', 'axis axis--x');

    const axisG = gContainer.call(xAxis);

    let textLength = Math.max(BandAxisRenderer.MAX_X_LABEL_LENGTH, x.bandwidth() - 8);
    let textWidth = textLength;

    const nodes = axisG.selectAll('text');
    if (this.config.rotateXLabels) {
      if (this.centerLabels) {
        axisG.call(a => {
          a.selectAll('.tick line').attr('transform', `translate(${x.bandwidth() / 2}, 0)`);
        });
      }

      const rotate = this.config.yAxisConfig.hide ? 90 : 60;
      const dy = rotate === 90 ? -8 : 0;
      const dx = 15;

      textLength = (xAxisHeight - dx) / Math.sin(rotate * (Math.PI / 180));
      textWidth = this.config.yAxisConfig.hide ? 10 : 17; // We could also calc this

      nodes.attr('dy', `${dy}px`).attr('dx', -dx).attr('transform', `rotate(-${rotate})`).style('text-anchor', 'end');
    }

    let labelDisplayDividor = 1;
    let labelStart = 0;
    if (nodes.size() * textWidth > width) {
      labelDisplayDividor = Math.ceil((nodes.size() * textWidth) / width);
      if (this.config.rotateXLabels) {
        labelStart = 1;
      } else {
        labelStart = -1;
      }
    }

    axisG.selectAll('text').each((_d, i, textNodes) => {
      const node = textNodes[i] as SVGTextElement;
      const text = d3.select(node);

      BandAxisRenderer.wrapText(text, textLength - 5);

      node.style.display = 'none';
      if ((i + labelStart) % labelDisplayDividor === 0) {
        node.style.display = 'inherit';
      }
    });

    gContainer.attr('transform', `translate(0, ${height})`);
    return { scale: x };
  }

  public calcMaximumBands(size: number, rendererConfig: RendererConfig): number {
    let neededSize = 20;
    if (this.config.breakdownMode === 'parallel') {
      neededSize = 40;
    } else if (rendererConfig.hasSecondaryData()) {
      neededSize = 30;
    }
    const primaryData = this.config.getDataArray(ChartDataType.primary);
    const items = primaryData!.getData().length;
    const bands = Math.min(Math.floor(size / neededSize), items);
    return bands;
  }

  public calcXHeight(): number {
    return this.config.xAxisConfig.hide ? 5 : this.config.rotateXLabels ? 70 : 30;
  }

  public renderY(height: number, width: number, limit?: number): { scale: d3.ScaleBand<string>; width: number } {
    const primaryData = this.config.getDataArray(ChartDataType.primary)!;

    const y = d3.scaleBand().range([height, 0]);
    const yAxis = d3.axisLeft(y).tickFormat(value => this.context.attributeFormatter(value) ?? '');

    y.domain(primaryData.getData(limit, true).map((d: ChartData) => d.label!));

    if (this.config.yAxisConfig.hide) {
      // return scale but do not render it
      return { scale: y, width: 0 };
    }

    const maxAllowedLength = Math.max(BandAxisRenderer.MAX_Y_LABEL_LENGTH, width / 3);
    const maxLabelWidth = Math.min(this.getMaxLabelLength(), maxAllowedLength);
    const axisWidth = maxLabelWidth + 10;

    const gContainerY = this.gElement.append('g').attr('class', 'axis axis--y').attr('transform', 'translate(0, 0)');

    const containerAxisY = gContainerY.call(yAxis);

    containerAxisY.call(a => {
      a.selectAll('.tick line').attr('transform', `translate(0,${y.bandwidth() / 2})`);
    });

    if (maxLabelWidth === maxAllowedLength) {
      gContainerY.selectAll('text').each((_d, i, nodes) => {
        const node = nodes[i] as SVGTextElement;
        const text = d3.select(node);

        BandAxisRenderer.wrapText(text, maxLabelWidth);
      });
    }

    // containerAxisY.call(g => g.select('.domain').remove());
    return { scale: y, width: axisWidth };
  }

  private getMaxLabelLength(): number {
    const labelLengths: number[] = [];
    // render labels, get width and remove them afterwards
    this.gElement
      .append('g')
      .selectAll('.temp-text')
      .data(this.config.getDataArray(ChartDataType.primary)!.getData())
      .enter()
      .append('text')
      .attr('font-family', 'sans-serif')
      .attr('font-size', '10px')
      .attr('opacity', 0.0)
      .text(d => this.context.attributeFormatter(d.label!) ?? '')
      .each(function () {
        const thisWidth = this.getComputedTextLength();
        labelLengths.push(thisWidth);
        this.remove(); // remove them just after displaying them
      });

    return Math.max(...labelLengths);
  }

  private static wrapText(e: d3.Selection<SVGTextElement, unknown, null, undefined>, width: number) {
    const node = e.node();
    if (node) {
      let textLength = node.getComputedTextLength();
      let text = e.text();
      while (textLength > width && text.length > 0) {
        text = text.slice(0, -1);
        e.text(`${text}...`);
        textLength = node.getComputedTextLength();
      }
    }
  }
}
